import React, { useEffect, useState } from 'react';
import TemplateCheckFormRenderer, { validateAnswers } from './TemplateCheckFormRenderer';
import CustomerSearchSelect from './CustomerSearchSelect';
import { toLocalDateTimeInput } from '../utils/format';
import { filesToAttachmentPayload } from '../utils/attachments';
import './LogCommercialActivityModal.css';

// Fallbacks only — the live lists come from Settings → Configurable Support
// Lists (commercial_activity_types / commercial_comms_methods /
// commercial_activity_outcomes). These apply when the config fetch fails or a
// list is unseeded/empty.
const FALLBACK_ACTIVITY_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'site_visit', label: 'Site Visit' },
  { value: 'demo', label: 'Demo' },
  { value: 'note', label: 'Note' },
  { value: 'quote_sent', label: 'Quote Sent' },
  { value: 'account_review', label: 'Account Review' }
];

const FALLBACK_COMMS_METHODS = [
  { value: 'phone', label: 'Phone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'teams', label: 'Teams' },
  { value: 'site_visit', label: 'Site visit' },
  { value: 'in_person', label: 'In person' },
  { value: 'other', label: 'Other' }
];

const FALLBACK_OUTCOMES = [
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
  { value: 'no_response', label: 'No Response' }
];

const activeConfigList = (data, key, fallback) => {
  const items = (data?.[key] || []).filter(item => item.active !== false);
  return items.length > 0
    ? items.map(({ value, label }) => ({ value, label }))
    : fallback;
};

const DEFAULT_CONTACTS = [];

const toDateTimeLocal = (value) => toLocalDateTimeInput(value || undefined) || toLocalDateTimeInput();

const labelFor = (options, value) => options.find(option => option.value === value)?.label || value;

function LogCommercialActivityModal({
  authFetch,
  customerId,
  customerName,
  opportunityId,
  initialContacts = DEFAULT_CONTACTS,
  mode = 'log',
  // When invoked from an opportunity, the parent can pass the pipeline stages
  // and current stage so the activity can also progress the deal (advance a
  // stage, or close it won/lost) in the same action. Omitted for customer-level
  // activity logging, where no progress selector is shown.
  stages = null,
  currentStage = '',
  onClose,
  onSaved
}) {
  const [saving, setSaving] = useState(false);
  const [progressStage, setProgressStage] = useState('');
  const [activityTypes, setActivityTypes] = useState(FALLBACK_ACTIVITY_TYPES);
  const [communicationMethods, setCommunicationMethods] = useState(FALLBACK_COMMS_METHODS);
  const [outcomes, setOutcomes] = useState(FALLBACK_OUTCOMES);
  // Optional sales-form attachment (Phase-2 engine): pick a sales context, the
  // matching published sales form resolves and renders, and its answers are
  // stored on the activity.
  const [resolvedForm, setResolvedForm] = useState(null); // { template_id, name, schema }
  const [formAnswers, setFormAnswers] = useState({});
  const [formResolveState, setFormResolveState] = useState(''); // '', 'resolving', 'missing'
  // A form that exists but could not be shown (two forms match, or the lookup
  // failed): said, never silently left out (Gareth 2026-10-21).
  const [formProblem, setFormProblem] = useState('');
  const [formErrors, setFormErrors] = useState([]);
  // Photos and files on the activity form, by question ('template:<key>').
  const [formAttachments, setFormAttachments] = useState({});
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [error, setError] = useState(null);
  const [customerLabel, setCustomerLabel] = useState(customerName || '');
  const [contacts, setContacts] = useState(initialContacts);
  const [formData, setFormData] = useState({
    customer_id: customerId || '',
    contact_id: '',
    free_contact_name: '',
    entry_type: 'call',
    communication_method: 'phone',
    occurred_at: toDateTimeLocal(),
    scheduled_at: toDateTimeLocal(),
    outcome: 'neutral',
    outcome_notes: '',
    purpose: '',
    follow_up_required: false,
    follow_up_date: ''
  });

  const isScheduleMode = mode === 'schedule';

  // Load the three configurable commercial lists; if a picked default was
  // deactivated in Settings, snap it to the first active value.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await authFetch('/api/v1/support/config-lists');
        if (!response.ok || cancelled) return;
        const body = await response.json();
        const types = activeConfigList(body.data, 'commercial_activity_types', FALLBACK_ACTIVITY_TYPES);
        const methods = activeConfigList(body.data, 'commercial_comms_methods', FALLBACK_COMMS_METHODS);
        const outcomeList = activeConfigList(body.data, 'commercial_activity_outcomes', FALLBACK_OUTCOMES);
        setActivityTypes(types);
        setCommunicationMethods(methods);
        setOutcomes(outcomeList);
        setFormData(prev => ({
          ...prev,
          entry_type: types.some(t => t.value === prev.entry_type) ? prev.entry_type : types[0].value,
          communication_method: methods.some(m => m.value === prev.communication_method) ? prev.communication_method : methods[0].value,
          outcome: outcomeList.some(o => o.value === prev.outcome) ? prev.outcome : outcomeList[0].value
        }));
      } catch (err) {
        // Non-fatal: the fallback lists stay in place.
      }
    };
    load();
    return () => { cancelled = true; };
  }, [authFetch]);

  // Activity-type forms (Gareth 2026-08-04): the form FOLLOWS the chosen
  // activity type — no separate picker. A published Activity Form template
  // targeting the type renders inline; no match, nothing extra shows.
  // Whether filling it is mandatory is the template's own required flags.
  useEffect(() => {
    setFormProblem('');
    setFormErrors([]);
    if (isScheduleMode || !formData.entry_type) {
      setResolvedForm(null);
      setFormAnswers({});
      setFormAttachments({});
      setFormResolveState('');
      return;
    }
    let cancelled = false;
    const resolve = async () => {
      setFormResolveState('resolving');
      try {
        const response = await authFetch(
          `/api/v1/check-form-templates/resolve?category=sales_form&salesContext=${encodeURIComponent(formData.entry_type)}`
        );
        if (cancelled) return;
        if (response.ok) {
          const tpl = await response.json();
          setResolvedForm({ template_id: tpl.template_id, name: tpl.name, schema: tpl.schema_json });
          setFormAnswers({});
          setFormAttachments({});
          setFormResolveState('');
        } else {
          // 404: no form for this activity type. Anything else is a form
          // that should be here and is not, so it is said.
          if (response.status === 409) {
            const body = await response.json().catch(() => ({}));
            if (cancelled) return;
            const names = (body.conflicting_templates || []).map(t => t.name).join(' and ');
            setFormProblem(`More than one form is set up for this activity type${names ? ` (${names})` : ''}, so none is shown. A manager can fix this in Settings > Check Forms.`);
          } else if (response.status !== 404) {
            setFormProblem('The form for this activity type could not be loaded. Check your signal, then choose the activity type again.');
          }
          setResolvedForm(null);
          setFormAnswers({});
          setFormAttachments({});
          setFormResolveState('');
        }
      } catch (err) {
        if (!cancelled) {
          setFormProblem('The form for this activity type could not be loaded. Check your signal, then choose the activity type again.');
          setResolvedForm(null);
          setFormResolveState('');
        }
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [authFetch, isScheduleMode, formData.entry_type]);

  useEffect(() => {
    if (!formData.customer_id) {
      setContacts(prev => (prev.length ? [] : prev));
      return;
    }

    if (customerId && formData.customer_id === customerId && initialContacts.length > 0) {
      setContacts(initialContacts);
      return;
    }

    const fetchContacts = async () => {
      setLoadingContacts(true);
      try {
        const response = await authFetch(`/api/v1/customers/${formData.customer_id}/contacts`);
        if (response.ok) {
          const data = await response.json();
          setContacts(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load contacts for activity logging:', err);
        setContacts([]);
      } finally {
        setLoadingContacts(false);
      }
    };

    fetchContacts();
  }, [authFetch, customerId, formData.customer_id, initialContacts]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'customer_id' ? { contact_id: '', free_contact_name: '' } : {}),
      ...(name === 'follow_up_required' && !checked ? { follow_up_date: '' } : {})
    }));
  };

  // Name for the success toast: the fixed-customer prop when launched from a
  // customer page, otherwise whatever was picked in the search box.
  const selectedCustomerName = customerName || customerLabel;

  const handleCustomerSelect = (row) => {
    if (row) setError(null);
    setCustomerLabel(row ? row.business_name : '');
    setFormData(prev => ({
      ...prev,
      customer_id: row ? row.customer_id : '',
      contact_id: '',
      free_contact_name: ''
    }));
  };

  const addFormFiles = async (stepKey, files, input) => {
    try {
      const picked = await filesToAttachmentPayload(files);
      setFormAttachments(prev => ({
        ...prev,
        [stepKey]: [...(prev[stepKey] || []), ...picked.map(item => ({ ...item, step_key: stepKey }))]
      }));
    } catch (err) {
      setError(err.message || 'The file could not be added.');
    } finally {
      if (input) input.value = '';
    }
  };

  const removeFormFile = (stepKey, index) => {
    setFormAttachments(prev => ({ ...prev, [stepKey]: (prev[stepKey] || []).filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    if (!formData.customer_id) {
      setError('Customer is required');
      setSaving(false);
      return;
    }

    if (isScheduleMode && !formData.purpose.trim()) {
      setError('Activity purpose is required');
      setSaving(false);
      return;
    }

    if (!isScheduleMode && formData.follow_up_required && !formData.follow_up_date) {
      setError('Follow-up date is required when follow-up is selected');
      setSaving(false);
      return;
    }

    // The activity form's required questions, as the server checks them too.
    if (!isScheduleMode && resolvedForm) {
      const validation = validateAnswers(resolvedForm.schema, formAnswers, { requireComplete: true, attachments: formAttachments });
      setFormErrors(validation.errors);
      if (!validation.valid) {
        setError(`${resolvedForm.name}: ${validation.errors[0].message}`);
        setSaving(false);
        return;
      }
    }

    const selectedContact = contacts.find(contact => contact.contact_id === formData.contact_id);
    const contactName = selectedContact?.full_name || formData.free_contact_name.trim();
    const activityLabel = labelFor(activityTypes, formData.entry_type);
    const methodLabel = isScheduleMode
      ? activityLabel
      : labelFor(communicationMethods, formData.communication_method);
    const outcomeLabel = labelFor(outcomes, formData.outcome);
    const scheduledDateOnly = String(formData.scheduled_at || '').split('T')[0];
    const followUpText = formData.follow_up_required
      ? `Follow-up required: ${formData.follow_up_date}`
      : 'Follow-up required: No';

    const bodyLines = isScheduleMode
      ? [
          `Communication method: ${methodLabel}`,
          contactName ? `Contact: ${contactName}` : null,
          `Scheduled date/time: ${formData.scheduled_at}`,
          `Purpose: ${formData.purpose.trim()}`,
          'Status: Scheduled'
        ].filter(Boolean)
      : [
          `Communication method: ${methodLabel}`,
          contactName ? `Contact: ${contactName}` : null,
          `Outcome: ${outcomeLabel}`,
          formData.outcome_notes ? `Outcome notes: ${formData.outcome_notes.trim()}` : null,
          followUpText
        ].filter(Boolean);

    try {
      const payload = {
        customer_id: formData.customer_id,
        opportunity_id: opportunityId || null,
        contact_id: formData.contact_id || null,
        entry_type: formData.entry_type,
        occurred_at: isScheduleMode ? new Date().toISOString() : new Date(formData.occurred_at).toISOString(),
        subject: isScheduleMode
          ? `Scheduled ${activityLabel} via ${methodLabel}${selectedCustomerName ? ` - ${selectedCustomerName}` : ''}`
          : `${activityLabel} via ${methodLabel}${selectedCustomerName ? ` - ${selectedCustomerName}` : ''}`,
        body: bodyLines.join('\n'),
        outcome: isScheduleMode ? null : formData.outcome,
        next_action: isScheduleMode
          ? formData.purpose.trim()
          : formData.follow_up_required ? `Commercial follow-up: ${activityLabel} via ${methodLabel}` : null,
        next_action_date: isScheduleMode
          ? scheduledDateOnly
          : formData.follow_up_required ? formData.follow_up_date : null
      };

      // Attach the activity-form answers when the chosen type resolved a
      // form. Store a self-contained snapshot (schema + answers + context)
      // so the activity renders read-only later regardless of subsequent
      // template edits. sales_context keeps its key name for historic
      // renderers; the value is the activity type.
      if (!isScheduleMode && resolvedForm) {
        payload.form_template_id = resolvedForm.template_id;
        payload.form_responses = {
          sales_context: formData.entry_type,
          template_name: resolvedForm.name,
          schema: resolvedForm.schema,
          answers: formAnswers,
          // The server stores them and files its own copy (timeline POST).
          attachments: Object.values(formAttachments).flat()
        };
      }

      const response = await authFetch('/api/v1/timeline', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save activity');
      }

      const savedActivity = await response.json();
      // Pass the chosen progress stage back so the parent applies it via the
      // existing PUT /opportunities/:id/stage flow (which handles won/lost + FX
      // + audit). Empty string = no stage change.
      onSaved(savedActivity, progressStage);
    } catch (err) {
      setError(err.message || 'Failed to save activity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content commercial-activity-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isScheduleMode ? 'Schedule Commercial Activity' : 'Log Commercial Activity'}</h2>
          <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="modal-error">{error}</div>}

          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="activity_customer_id">Customer *</label>
              {customerId ? (
                <input id="activity_customer_id" value={customerName || 'Selected customer'} disabled />
              ) : (
                <div className="activity-customer-search">
                  {/* Every end customer — Prospect, Active or Dormant (Gareth
                      2026-09-14). The default kind excludes only distributors. */}
                  <CustomerSearchSelect
                    id="activity_customer_id"
                    minChars={1}
                    value={formData.customer_id}
                    initialLabel={customerLabel}
                    onSelect={handleCustomerSelect}
                  />
                </div>
              )}
              <input
                type="hidden"
                name="customer_id"
                value={formData.customer_id}
                readOnly
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="activity_contact_id">Contact person</label>
                <select
                  id="activity_contact_id"
                  name="contact_id"
                  value={formData.contact_id}
                  onChange={handleChange}
                  disabled={!formData.customer_id || loadingContacts || contacts.length === 0}
                >
                  <option value="">
                    {loadingContacts ? 'Loading contacts...' : contacts.length === 0 ? 'No saved contacts' : 'Select contact...'}
                  </option>
                  {contacts.map(contact => (
                    <option key={contact.contact_id} value={contact.contact_id}>
                      {contact.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="activity_free_contact_name">Contact name if not listed</label>
                <input
                  id="activity_free_contact_name"
                  name="free_contact_name"
                  value={formData.free_contact_name}
                  onChange={handleChange}
                  placeholder="e.g. Farm manager"
                  disabled={Boolean(formData.contact_id)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="activity_entry_type">Activity type *</label>
                <select id="activity_entry_type" name="entry_type" value={formData.entry_type} onChange={handleChange} required>
                  {activityTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {!isScheduleMode && (
                <div className="form-group">
                  <label htmlFor="activity_communication_method">Communication method *</label>
                  <select
                    id="activity_communication_method"
                    name="communication_method"
                    value={formData.communication_method}
                    onChange={handleChange}
                    required
                  >
                    {communicationMethods.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="activity_occurred_at">
                  {isScheduleMode ? 'Scheduled date/time *' : 'Date/time activity happened *'}
                </label>
                <input
                  type="datetime-local"
                  id="activity_occurred_at"
                  name={isScheduleMode ? 'scheduled_at' : 'occurred_at'}
                  value={isScheduleMode ? formData.scheduled_at : formData.occurred_at}
                  onChange={handleChange}
                  required
                />
              </div>

              {!isScheduleMode && (
                <div className="form-group">
                  <label htmlFor="activity_outcome">Outcome *</label>
                  <select id="activity_outcome" name="outcome" value={formData.outcome} onChange={handleChange} required>
                    {outcomes.map(outcome => (
                      <option key={outcome.value} value={outcome.value}>{outcome.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {isScheduleMode ? (
              <div className="form-group">
                <label htmlFor="activity_purpose">Activity purpose *</label>
                <textarea
                  id="activity_purpose"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  rows={4}
                  placeholder="What should be discussed or done?"
                  required
                />
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="activity_outcome_notes">Outcome notes</label>
                <textarea
                  id="activity_outcome_notes"
                  name="outcome_notes"
                  value={formData.outcome_notes}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Short summary only. Keep the customer timeline useful, not noisy."
                />
              </div>
            )}

            {!isScheduleMode && (
              <div className="commercial-follow-up-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="follow_up_required"
                    checked={formData.follow_up_required}
                    onChange={handleChange}
                  />
                  Follow-up required
                </label>

                {formData.follow_up_required && (
                  <div className="form-group">
                    <label htmlFor="activity_follow_up_date">Follow-up date *</label>
                    <input
                      type="date"
                      id="activity_follow_up_date"
                      name="follow_up_date"
                      value={formData.follow_up_date}
                      onChange={handleChange}
                      required
                    />
                  </div>
                )}
              </div>
            )}

            {!isScheduleMode && Array.isArray(stages) && stages.length > 0 && (
              <div className="form-group activity-progress-group">
                <label htmlFor="activity_progress_stage">Progress this opportunity</label>
                <select
                  id="activity_progress_stage"
                  value={progressStage}
                  onChange={(e) => setProgressStage(e.target.value)}
                >
                  <option value="">No stage change</option>
                  {stages.map(stage => {
                    const suffix = stage.is_closed_won ? ' — Won' : stage.is_closed_lost ? ' — Lost' : '';
                    const isCurrent = stage.stage_name === currentStage;
                    return (
                      <option key={stage.stage_name} value={stage.stage_name}>
                        {stage.stage_name}{suffix}{isCurrent ? ' (current)' : ''}
                      </option>
                    );
                  })}
                </select>
                <p className="activity-progress-hint">Optionally advance the stage, or close the deal won/lost, as part of logging this activity.</p>
              </div>
            )}

            {/* The form follows the chosen activity type — nothing to pick */}
            {!isScheduleMode && formResolveState === 'resolving' && (
              <p className="activity-progress-hint">Checking for a form for this activity type…</p>
            )}
            {!isScheduleMode && formProblem && (
              <p className="activity-form-problem" role="alert">{formProblem}</p>
            )}
            {!isScheduleMode && resolvedForm && (
              <div className="form-group activity-sales-form-group">
                <div className="activity-sales-form-render">
                  <div className="activity-sales-form-title">{resolvedForm.name}</div>
                  <TemplateCheckFormRenderer
                    schema={resolvedForm.schema}
                    answers={formAnswers}
                    onAnswersChange={setFormAnswers}
                    showValidationErrors={formErrors.length > 0}
                    validationErrors={formErrors}
                    stepAttachments={formAttachments}
                    onAddPhotos={addFormFiles}
                    onRemovePhoto={removeFormFile}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Saving...' : isScheduleMode ? 'Schedule Activity' : 'Save Activity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LogCommercialActivityModal;
